import { getSubscriptionLabel, PriceLabel } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SelectableRow } from '@/components/ui/selectable-row'
import { BillingPeriodStatus, SubscriptionData } from '@/domain/subscriptions/subscriptions'
import { AlertCircle, CheckSquare, DollarSign, X } from 'lucide-react'
import { useState } from 'react'

interface SelectBillingPeriodsModalProps {
  isOpen: boolean
  onClose: () => void
  subscriptions: SubscriptionData[]
  onPayMultiple: (subscriptionId: string, billingPeriodIds: string[]) => void
  isPending: boolean
  onSuccess?: () => void
}

export const SelectBillingPeriodsModal = ({
  isOpen,
  onClose,
  subscriptions,
  onPayMultiple,
  isPending,
  onSuccess,
}: SelectBillingPeriodsModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const pendingPeriods = subscriptions.filter(s => {
    if (s.periodStatus === BillingPeriodStatus.CANCELLED) return false
    if (s.outstandingAmount <= 0) return false
    return true
  })

  // console.log(subscriptions)

  const totalSelected = selectedIds.size
  const totalAmount = pendingPeriods
    .filter(s => selectedIds.has(s.billingPeriodId))
    .reduce((sum, s) => sum + s.outstandingAmount, 0)

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === pendingPeriods.length) {
        return new Set()
      }
      return new Set(pendingPeriods.map(s => s.billingPeriodId))
    })
  }

  const handleConfirm = () => {
    if (selectedIds.size === 0) return
    onPayMultiple(
      subscriptions.find(s => selectedIds.has(s.billingPeriodId))?.subscriptionId || '',
      Array.from(selectedIds),
    )
    onClose()
    setSelectedIds(new Set())
    onSuccess?.()
  }

  if (pendingPeriods.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pagar períodos</DialogTitle>
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
              <AlertCircle className="h-4 w-4" />
              No hay períodos pendientes para seleccionar
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pagar múltiples períodos</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 my-4">
          <div className="flex items-center justify-between pb-2 border-b">
            <span className="text-sm text-muted-foreground font-medium">
              {pendingPeriods.length} período {pendingPeriods.length !== 1 ? 's' : ''} pendiente{pendingPeriods.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
              disabled={isPending}
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              Seleccionar todos
            </Button>
          </div>

          {pendingPeriods.map(sub => {
            const isSelected = selectedIds.has(sub.billingPeriodId)
            return (
              <SelectableRow
                key={sub.billingPeriodId}
                checked={isSelected}
                disabled={isPending}
                onCheckChange={() => toggleSelection(sub.billingPeriodId)}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSelected}
                    disabled={isPending}
                  />
                  <div>
                    <span className="mt-1 inline-block text-md">
                      <span className='font-medium'>Período </span>
                      <span className='font-medium font-mono'>{getSubscriptionLabel(sub.periodStartDate, sub.periodEndDate, sub.billingFrequency)}</span>
                    </span>
                    <div className="text-sm ">
                      {new Date(sub.periodStartDate).toLocaleDateString('es-AR')} —{' '}
                      {new Date(sub.periodEndDate).toLocaleDateString('es-AR')}
                    </div>
                  </div>
                </div>
                <PriceLabel price={sub.outstandingAmount} />
              </SelectableRow>
            )
          })}
        </div>

        <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
          <span className="text-sm font-medium">Total a pagar:</span>
          <span className="text-lg font-bold font-mono">${totalAmount.toLocaleString('es-AR')}</span>
        </div>

        <DialogFooter className="mt-4">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0 || isPending}
            >
              <DollarSign className="h-3.5 w-3.5 mr-1.5" />
              Pagar {totalSelected > 0 ? `(${totalSelected})` : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
